// =====================================================================
// My Feed Reader — typed helpers + edge-function wrappers.
//
// Scoping mirrors My Library: the active workspace owns reads/mutations
// in `single`/`isolated` mode; in `shared` mode the inbox pools every
// workspace the user can access.
// =====================================================================
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { LibraryMode } from "@/lib/tier";
import type { Workspace } from "@/hooks/useWorkspace";

export type FeedSubscription = Tables<"content_feed_subscriptions">;
export type FeedItem = Tables<"content_feed_items">;

export type FeedFilter = "unread" | "starred" | "archive" | "all";

// Returns the array of workspace IDs the inbox / subscription list should query.
export const scopeFilter = (
  mode: LibraryMode,
  active: Workspace | null,
  workspaces: Workspace[],
): string[] => {
  if (!active) return [];
  if (mode === "shared" && workspaces.length > 1) return workspaces.map((w) => w.id);
  return [active.id];
};

// ---- subscriptions ------------------------------------------------------

export const fetchSubscriptions = async (workspaceIds: string[]): Promise<FeedSubscription[]> => {
  if (!workspaceIds.length) return [];
  const { data, error } = await supabase
    .from("content_feed_subscriptions")
    .select("*")
    .in("workspace_id", workspaceIds)
    .order("title", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const fetchUnreadCounts = async (
  subscriptionIds: string[],
): Promise<Record<string, number>> => {
  const out: Record<string, number> = {};
  if (!subscriptionIds.length) return out;
  // One small query per subscription would be wasteful at scale; fetch all
  // unread item rows and tally client-side. RLS already restricts to the
  // user's workspaces.
  const { data, error } = await supabase
    .from("content_feed_items")
    .select("subscription_id")
    .in("subscription_id", subscriptionIds)
    .is("read_at", null)
    .is("archived_at", null)
    .is("deleted_at", null);
  if (error) throw new Error(error.message);
  for (const row of data ?? []) {
    out[row.subscription_id] = (out[row.subscription_id] ?? 0) + 1;
  }
  return out;
};

export const addSubscription = async (workspaceId: string, url: string) => {
  const { data, error } = await supabase.functions.invoke("feed-add-subscription", {
    body: { workspace_id: workspaceId, url },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as { subscription: FeedSubscription; imported: number };
};

export const syncSubscription = async (subscriptionId: string) => {
  const { data, error } = await supabase.functions.invoke("feed-sync", {
    body: { subscription_id: subscriptionId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as { ok: true; not_modified: boolean; new_items: number };
};

export const deleteSubscription = async (subscriptionId: string) => {
  const { data, error } = await supabase.functions.invoke("feed-delete-subscription", {
    body: { subscription_id: subscriptionId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return true;
};

// ---- items --------------------------------------------------------------

export type FetchItemsArgs = {
  workspaceIds: string[];
  filter: FeedFilter;
  subscriptionId?: string | null;
};

export const fetchFeedItems = async ({
  workspaceIds,
  filter,
  subscriptionId,
}: FetchItemsArgs): Promise<FeedItem[]> => {
  if (!workspaceIds.length) return [];
  let q = supabase
    .from("content_feed_items")
    .select("*")
    .in("workspace_id", workspaceIds)
    .is("deleted_at", null)
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(200);
  if (subscriptionId) q = q.eq("subscription_id", subscriptionId);

  switch (filter) {
    case "unread":
      q = q.is("read_at", null).is("archived_at", null);
      break;
    case "starred":
      q = q.not("starred_at", "is", null);
      break;
    case "archive":
      q = q.not("archived_at", "is", null);
      break;
    case "all":
    default:
      break;
  }

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data ?? [];
};

const updateItem = async (id: string, patch: Partial<FeedItem>) => {
  const { error } = await supabase.from("content_feed_items").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
};

export const markRead = (id: string, read: boolean) =>
  updateItem(id, { read_at: read ? new Date().toISOString() : null });

export const toggleStar = (id: string, starred: boolean) =>
  updateItem(id, { starred_at: starred ? new Date().toISOString() : null });

export const setArchived = (id: string, archived: boolean) =>
  updateItem(id, { archived_at: archived ? new Date().toISOString() : null });

// Soft-delete: marks the item as deleted so it disappears from all views.
// Sync uses `ignoreDuplicates: true` on (subscription_id, external_id), so
// a soft-deleted row is NOT revived when the same external_id is re-fetched.
export const deleteFeedItem = (id: string) =>
  updateItem(id, { deleted_at: new Date().toISOString() } as Partial<FeedItem>);

export const promoteToLibrary = async (itemId: string) => {
  const { data, error } = await supabase.functions.invoke("feed-promote-item", {
    body: { item_id: itemId },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as { ok: true; library_item_id: string; already_imported?: boolean };
};

// ---- article extraction (on-open, frictionless) ------------------------

export type ExtractStatus =
  | "ok"
  | "paywall"
  | "failed"
  | "skipped_insecure"
  | "blocked_private"
  | "cap_reached"
  | "too_large"
  | "timeout";

export type ExtractResponse = {
  status: ExtractStatus;
  html: string | null;
  cached?: boolean;
  cap?: number;
  used?: number;
};

/**
 * Heuristic: does this feed item look like a publisher excerpt rather than
 * the full article body? Cheap — runs on already-fetched data, no network.
 */
export const isSummaryFeedItem = (item: FeedItem): boolean => {
  const html = (item.content_html ?? item.summary ?? "").trim();
  if (!html) return false; // nothing to extract from anyway
  if (html.length >= 1500) return false; // likely full text
  const hasMarker = /\[\u2026\]|\[\.\.\.\]|&hellip;|Continue reading|Read more/i.test(html);
  return hasMarker || html.length < 800;
};

/**
 * Invokes feed-extract-article. Returns the cached or freshly-extracted
 * body. Never throws on extraction failures — the caller should fall back
 * to the feed summary based on the returned status.
 */
export const extractArticleHtml = async (itemId: string): Promise<ExtractResponse> => {
  const { data, error } = await supabase.functions.invoke("feed-extract-article", {
    body: { item_id: itemId },
  });
  if (error) {
    return { status: "failed", html: null };
  }
  return data as ExtractResponse;
};

/**
 * Fire-and-forget prefetch used to warm the server-side extraction cache
 * when the user hovers or keyboard-navigates onto a summary item. Idempotent
 * per session via an in-memory Set so hover + select don't double-fire and
 * we don't burn the daily extraction quota on items we've already requested,
 * already have a cached body for, or that previously failed.
 */
const prefetchedIds = new Set<string>();
type PrefetchableItem = Pick<FeedItem, "id" | "extracted_html" | "extraction_status"> &
  Partial<FeedItem>;
export const prefetchExtraction = (item: PrefetchableItem | null | undefined) => {
  if (!item || !item.id) return;
  if (prefetchedIds.has(item.id)) return;
  // Already cached or already attempted (ok or terminal failure) — nothing to do.
  if (item.extraction_status) return;
  // Only summary-shaped items benefit from extraction.
  if (!isSummaryFeedItem(item as FeedItem)) return;
  prefetchedIds.add(item.id);
  void extractArticleHtml(item.id).catch(() => {
    // Allow a future retry on transient network failure.
    prefetchedIds.delete(item.id);
  });
};

// ---- bulk + helpers -----------------------------------------------------

// Mark a list of feed items as read (or unread) in a single round-trip.
export const markManyRead = async (ids: string[], read: boolean) => {
  if (!ids.length) return;
  const { error } = await supabase
    .from("content_feed_items")
    .update({ read_at: read ? new Date().toISOString() : null })
    .in("id", ids);
  if (error) throw new Error(error.message);
};

// Total unread count across the given workspaces (used by the sidebar nav badge).
export const fetchUnreadTotal = async (workspaceIds: string[]): Promise<number> => {
  if (!workspaceIds.length) return 0;
  const { count, error } = await supabase
    .from("content_feed_items")
    .select("id", { count: "exact", head: true })
    .in("workspace_id", workspaceIds)
    .is("read_at", null)
    .is("archived_at", null)
    .is("deleted_at", null);
  if (error) throw new Error(error.message);
  return count ?? 0;
};
// Cross-component event so the sidebar badge updates instantly after page mutations.
export const FEED_ITEMS_CHANGED_EVENT = "feed-reader:items-changed";
export const emitFeedItemsChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(FEED_ITEMS_CHANGED_EVENT));
  }
};

/** Today's per-workspace article-extraction count (UTC day). 0 if no row. */
export const fetchTodayExtractionCount = async (workspaceId: string): Promise<number> => {
  if (!workspaceId) return 0;
  const day = new Date().toISOString().slice(0, 10);
  const { data } = await supabase
    .from("content_feed_extraction_usage")
    .select("count")
    .eq("workspace_id", workspaceId)
    .eq("day", day)
    .maybeSingle();
  return (data?.count as number | undefined) ?? 0;
};

